import { computeFileHash } from "./fileHash.ts";
import {
  getReferenceImage,
  storeReferenceImage,
} from "./persistence/referenceImageDB.ts";

/** Maximum dimension (width or height) for reference images. */
const MAX_DIMENSION = 1024;

/** WebP compression quality (0–1). Lower = smaller files, more artifacts. */
const WEBP_QUALITY = 0.6;

/**
 * Load a File/Blob as an HTMLImageElement.
 */
function loadImage(source: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

/**
 * Resize an image so neither dimension exceeds MAX_DIMENSION.
 * Returns a blob of the resized image (PNG) for further compression.
 * If the image is already within bounds, returns the original source.
 */
async function resizeImage(source: File | Blob): Promise<File | Blob> {
  const img = await loadImage(source);

  const w = img.naturalWidth;
  const h = img.naturalHeight;

  if (w <= MAX_DIMENSION && h <= MAX_DIMENSION) {
    return source;
  }

  const ratio = Math.min(MAX_DIMENSION / w, MAX_DIMENSION / h);
  const newW = Math.round(w * ratio);
  const newH = Math.round(h * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, newW, newH);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas toBlob failed (resize)"));
    }, "image/png");
  });
}

/**
 * Compress an image to WebP.
 * Returns the compressed blob.
 */
async function compressImage(source: File | Blob): Promise<Blob> {
  const img = await loadImage(source);

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob failed"));
      },
      "image/webp",
      WEBP_QUALITY,
    );
  });
}

/**
 * Compress, deduplicate, and store a reference image locally in IndexedDB.
 *
 * If an identical image already exists locally (by SHA-256 hash),
 * the existing entry is reused without creating a duplicate.
 *
 * Returns the content hash (used as imageKey) and a blob URL for immediate display.
 */
export async function compressAndStoreReferenceImage(
  source: File | Blob,
  name?: string,
): Promise<{ imageKey: string; imageUrl: string }> {
  // 1. Resize large images, then compress to WebP
  const resized = await resizeImage(source);
  const compressed = await compressImage(resized);

  // 2. Compute hash for deduplication
  const buffer = await compressed.arrayBuffer();
  const hash = await computeFileHash(buffer);

  // 3. Check if this exact image already exists locally
  const existing = await getReferenceImage(hash);
  if (existing) {
    const imageUrl = URL.createObjectURL(existing.blob);
    return { imageKey: hash, imageUrl };
  }

  // 4. Store in IndexedDB
  const displayName = name ?? `Reference ${new Date().toLocaleDateString()}`;
  await storeReferenceImage(hash, displayName, compressed);

  // 5. Create blob URL for immediate display
  const imageUrl = URL.createObjectURL(compressed);
  return { imageKey: hash, imageUrl };
}
